/*
 * gs-ladder.js
 *
 * Tier selection, price repaint, and frequency-list construction for
 * gs-quantity-ladder.
 *
 * The rule this file exists to enforce: a tier is only ever offered the
 * selling plans allocated to its own variant. The frequency list is REBUILT
 * from the section payload on every tier change, never filtered in place,
 * because filtering leaves another tier's option selectable and that is how a
 * customer gets charged against the wrong plan.
 *
 * Reads only its own section's JSON payload. Elixir publishes
 * window.subscriptionContext and window.__subscriptionPlansData; those are
 * never read or mutated here, because a theme update rebuilds them.
 */
(function () {
  'use strict';

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function init(root) {
    var dataEl = root.querySelector('[data-gs-ladder-data]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      // A malformed payload must not leave a half-wired ladder that submits
      // the wrong thing. Leave the markup inert and say why.
      console.error('[gs-ladder] payload did not parse; ladder left inert', e);
      return;
    }

    var tiers = data.tiers || [];
    var byVariant = {};
    tiers.forEach(function (t) { byVariant[String(t.variantId)] = t; });

    var variantInput = root.querySelector('[data-gs-variant-input]');
    // Resolved on every use, not cached. Seal's widget script rewrites the
    // product form after we render: it removes our hidden selling_plan input
    // and substitutes its own, carrying data-sls-selling_plan. Caching the
    // node at boot meant applyMode() found nothing and returned early, so the
    // ladder silently stopped setting the plan at all and whatever Seal's own
    // hidden, product-scoped controls decided was submitted instead. That is
    // precisely the behaviour this ladder exists to prevent.
    //
    // Looking it up by name each time means we drive whichever input survives.
    function getPlanInput() {
      return root.querySelector('[data-gs-plan-input]')
          || root.querySelector('input[name="selling_plan"]');
    }
    var purchase = root.querySelector('[data-gs-purchase]');
    var freqWrap = root.querySelector('[data-gs-frequency]');
    var freqSelect = root.querySelector('[data-gs-frequency-select]');
    var modes = root.querySelectorAll('[data-gs-mode]');

    function selectedTier() {
      var checked = root.querySelector('[data-gs-tier]:checked');
      return checked ? byVariant[String(checked.value)] : null;
    }

    function currentMode() {
      var m = root.querySelector('[data-gs-mode]:checked');
      return m ? m.value : 'onetime';
    }

    // Rebuild, never filter. Any option left over from a previous tier is a
    // plan belonging to a different variant.
    function rebuildFrequencies(tier) {
      if (!freqSelect) return;
      while (freqSelect.firstChild) freqSelect.removeChild(freqSelect.firstChild);

      var plans = (tier && tier.plans) || [];
      plans.forEach(function (p) {
        var opt = document.createElement('option');
        opt.value = String(p.id);
        // Label the way the live store does: the plan's first option value
        // ("month", "2 months") read as "Every month". Seal's internal plan name
        // is the fallback, never the first choice.
        var label = String(p.opt || '').trim();
        if (label && !/^every/i.test(label)) label = 'Every ' + label;
        if (!label) label = p.name;
        // No price in the label: the subscribe option shows it, multiplied by
        // the quantity, where a per-pack figure here would disagree with it.
        opt.textContent = label;
        opt.setAttribute('data-price', String(p.price));
        freqSelect.appendChild(opt);
      });

      var hasPlans = plans.length > 0;
      if (purchase) purchase.hidden = !hasPlans;
      if (!hasPlans) {
        // No allocations on this variant: subscribing is not offered at all
        // rather than offered and broken.
        var onetime = root.querySelector('[data-gs-mode][value="onetime"]');
        if (onetime) onetime.checked = true;
      }
    }

    // Prices on the two purchase options, for the selected pack and, when
    // subscribing, the selected frequency. The saving compares the plan's
    // per-delivery price with the pack's one-time price, the same arithmetic
    // the section renders on load.
    var qtyInput = root.querySelector('[data-gs-qty-input]');

    function quantity() {
      var n = parseInt(qtyInput ? qtyInput.value : '1', 10);
      if (!(n >= 1)) n = 1;
      if (n > 99) n = 99;
      return n;
    }

    function repaintPrices(tier) {
      if (!tier) return;
      var qty = quantity();
      var oneNow = root.querySelector('[data-gs-onetime-price]');
      if (oneNow) oneNow.textContent = money(tier.price * qty, data.currency);

      var plans = tier.plans || [];
      var plan = plans[0];
      if (freqSelect && freqSelect.value) {
        plans.forEach(function (p) { if (String(p.id) === freqSelect.value) plan = p; });
      }
      if (!plan) return;

      var subPrice = plan.perDelivery || plan.price;
      var pct = tier.price > 0 && subPrice < tier.price
        ? Math.round((tier.price - subPrice) * 100 / tier.price)
        : 0;

      var now = root.querySelector('[data-gs-sub-now]');
      var was = root.querySelector('[data-gs-sub-was]');
      var save = root.querySelector('[data-gs-sub-save]');
      var pctEl = root.querySelector('[data-gs-sub-pct]');
      if (now) now.textContent = money(subPrice * qty, data.currency);
      if (was) {
        was.textContent = money(tier.price * qty, data.currency);
        was.hidden = pct <= 0;
      }
      if (pctEl) pctEl.textContent = String(pct);
      if (save) save.hidden = pct <= 0;
    }

    function applyMode() {
      var subscribing = currentMode() === 'subscribe';
      if (freqWrap) freqWrap.hidden = !subscribing;

      var planInput = getPlanInput();
      if (!planInput) return;

      if (subscribing && freqSelect && freqSelect.value) {
        planInput.value = freqSelect.value;
        planInput.disabled = false;   // disabled inputs are not submitted
      } else {
        planInput.value = '';
        planInput.disabled = true;
      }
    }

    function repaint(tier) {
      if (!tier) return;
      if (variantInput) variantInput.value = String(tier.variantId);

      root.querySelectorAll('[data-gs-tier]').forEach(function (r) {
        var tile = r.closest('.gs-ladder__tile');
        if (tile) tile.classList.toggle('is-selected', r.checked);
      });

      rebuildFrequencies(tier);
      repaintPrices(tier);
      applyMode();
    }

    root.querySelectorAll('[data-gs-tier]').forEach(function (r) {
      r.addEventListener('change', function () { repaint(selectedTier()); });
    });
    modes.forEach(function (m) { m.addEventListener('change', applyMode); });
    if (freqSelect) {
      freqSelect.addEventListener('change', function () {
        repaintPrices(selectedTier());
        applyMode();
      });
    }

    // Quantity stepper. The input is the form's own quantity field, so what is
    // submitted is what is shown; the buttons only step it within 1 to 99.
    if (qtyInput) {
      root.querySelectorAll('[data-gs-qty-step]').forEach(function (b) {
        b.addEventListener('click', function () {
          qtyInput.value = String(Math.min(99, Math.max(1, quantity() + parseInt(b.getAttribute('data-gs-qty-step'), 10))));
          repaintPrices(selectedTier());
        });
      });
      qtyInput.addEventListener('change', function () {
        qtyInput.value = String(quantity());
        repaintPrices(selectedTier());
      });
      qtyInput.addEventListener('input', function () { repaintPrices(selectedTier()); });
    }

    // Seal swaps the plan input in after we have already run. Watch the form
    // and re-apply whenever the field is replaced, so the last word on
    // selling_plan is always the tier the customer actually chose.
    var form = root.querySelector('form[action*="/cart/add"]');
    if (form && window.MutationObserver) {
      var reasserting = false;
      new MutationObserver(function () {
        if (reasserting) return;         // our own write must not re-trigger us
        reasserting = true;
        applyMode();
        reasserting = false;
      }).observe(form, { childList: true, subtree: true });
    }

    // Honour ?variant= so a collection card can preselect a pack, which is
    // what Johannes asked for on 2026-08-28.
    try {
      var wanted = new URLSearchParams(window.location.search).get('variant');
      if (wanted && byVariant[wanted]) {
        var radio = root.querySelector('[data-gs-tier][value="' + wanted + '"]');
        if (radio && !radio.disabled) radio.checked = true;
      }
    } catch (e) { /* preselection is a convenience, never a hard failure */ }

    repaint(selectedTier());
  }

  function boot() {
    document.querySelectorAll('[data-gs-ladder]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-init when the theme editor re-renders this section.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-gs-ladder]');
    if (root) init(root);
  });
})();
